/**
 * @file Shared types used by `@members/pg-search`
 */
import { invert } from 'ramda';
import type React from 'react';
import { createContext } from 'react';
import type { Event } from './events';
import type { State } from './state';

type SearchPageContext = {
  pageState: State;
  dispatch: React.Dispatch<Event>;
  // Excluded from `pageState` since nothing in the reducer needs to interact with this.
  popularQueries?: string[];
};

const noop = () => null;

/**
 * Wraps the Class Search page state in a context for the search page and its descendants (e.g. LibraryGrid)
 */
export const SearchPageContext = createContext<SearchPageContext>({
  dispatch: noop,
  pageState: {
    status: 'preload',
    trackedAnalytics: false,
    queryCounter: 0,
    previousPage: undefined,
  },
});

/**
 * Query Params Related
 */

export type QueryParams = Record<string, string | undefined>;

/**
 * Ensure only keys in State are supported.
 */
export type SupportedQueryParams = Pick<
  State,
  'query' | 'artistId' | 'fromCategory' | 'searchType' | 'artistListPosition'
>;

type SupportedKeys = keyof SupportedQueryParams;

/**
 * Ensure shortcut keys are unique.
 */
type ShortcutKeys = 'q' | 'id' | 'c' | 't' | 'p';

export const StateKeyForShortcut: Record<ShortcutKeys, SupportedKeys> = {
  q: 'query',
  id: 'artistId',
  c: 'fromCategory',
  t: 'searchType',
  p: 'artistListPosition',
};

/**
 * Map `State` keys to shortcut strings.
 */
export const ShortcutForStateKey = (invert(StateKeyForShortcut) as unknown) as Record<
  SupportedKeys,
  ShortcutKeys
>;

/**
 * Only iterate through known shortcut keys, ignore everything else.
 */
export const ShortcutKeysArray = (Object.keys(
  StateKeyForShortcut,
) as unknown) as ShortcutKeys[];
